import React from "react";
import { classNames } from "components/utils";
import * as Tooltip from "@radix-ui/react-tooltip";

interface RackCardLabelProps {
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  color?: "green" | "yellow" | "orange";
  showLight?: boolean;
  lightningBolt?: boolean; // When true, shows a lit-up lightning bolt instead of circle
  style?: "dark" | "light";
  tooltip?: string;
}

const RackCardLabel: React.FC<RackCardLabelProps> = ({
  label,
  icon,
  color,
  showLight = true,
  lightningBolt = false,
  style = "dark",
  tooltip,
}) => {
  const config = {
    green: {
      lightColor: "bg-green-500",
      boxShadowColor: "green",
      textColor: "text-green-400",
    },
    yellow: {
      lightColor: "bg-yellow-500",
      boxShadowColor: "yellow",
      textColor: "text-yellow-400",
    },
    orange: {
      lightColor: "bg-orange-500",
      boxShadowColor: "orange",
      textColor: "text-orange-400",
    },
  };

  const lightColor = color ? config[color].lightColor : "";
  const boxShadowColor = color ? config[color].boxShadowColor : "";
  const textColor = color ? config[color].textColor : "";

  const content = (
    <div className={classNames("inline-flex items-center gap-1.5")}>
      {color && !lightningBolt && showLight && (
        <div className="px-0.5">
          <div
            className={classNames(
              "w-2 h-2 rounded-full animate-pulse",
              lightColor
            )}
            style={{
              boxShadow: `0 0 4px ${boxShadowColor}`,
              animationDuration: "1s",
            }}
          />
        </div>
      )}
      <div
        className={classNames(
          "leading-none rounded-md text-sm text-primary font-medium rounded-md text-primary flex flex-row items-center gap-2 py-1 px-2 shadow-md text-wrap",
          style === "light" ? "bg-black/20" : "bg-black/40"
        )}
        style={{
          border: "1px inset #000000BA",
        }}
      >
        {icon}
        {label}
      </div>
    </div>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>{content}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            className="z-50 max-w-xs rounded-md bg-black px-3 py-2 text-xs text-white shadow-md"
          >
            {tooltip}
            <Tooltip.Arrow className="fill-black" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default RackCardLabel;
